import { useEffect, useRef, useState } from 'react';

import { DateTime } from 'luxon';

import { canCancelBooking, parseDate } from '@common/helpers/academyReworkings';
import getRequestError from '@common/helpers/getReworkingRequestError';
import useModel from '@common/helpers/useModel';
import StudentMeets from '@common/models/AcademyReworkings/StudentMeets';
import { BookedMeet } from '@common/types/academyReworkings';

export default function useMyBookings(
	studentId: number | undefined,
	fallbackError: string,
	onCancelled?: () => Promise<unknown>,
) {
	const meets = useModel(StudentMeets, { studentId: studentId || 0 }, [studentId], {
		disableFetch: true,
	});
	const bookings = meets.data;
	const { loading } = meets;
	const [error, setError] = useState<string | null>(null);
	const [revision, setRevision] = useState(0);
	const [target, setTarget] = useState<BookedMeet | null>(null);
	const [cancelError, setCancelError] = useState<string | null>(null);
	const cancelling = meets.saving;
	const [now, setNow] = useState(DateTime.now());
	const submitting = useRef(false);

	useEffect(() => {
		const timer = setInterval(() => setNow(DateTime.now()), 15000);
		return () => clearInterval(timer);
	}, []);

	useEffect(() => {
		let active = true;
		setError(null);
		if (studentId) {
			meets.fetch().catch((err) => {
				if (active) setError(getRequestError(err, fallbackError));
			});
		}
		return () => {
			active = false;
		};
	}, [meets, revision]);

	const upcoming = bookings
		.filter(
			(booking) =>
				booking.service_id === 1 &&
				booking.type === 'working_off' &&
				[0, 3].includes(booking.status) &&
				parseDate(booking.end_dt) > now,
		)
		.sort((a, b) => parseDate(a.start_dt).toMillis() - parseDate(b.start_dt).toMillis());
	const blocked = !!target && !canCancelBooking(target.start_dt, now);

	const requestCancel = (booking: BookedMeet) => {
		setNow(DateTime.now());
		setCancelError(null);
		setTarget(booking);
	};
	const closeDialog = () => {
		if (!submitting.current) setTarget(null);
	};
	const confirmCancel = async () => {
		if (!target || submitting.current) return;
		const currentTime = DateTime.now();
		setNow(currentTime);
		if (!canCancelBooking(target.start_dt, currentTime)) return;
		submitting.current = true;
		setCancelError(null);
		try {
			await meets.remove(target.id);
			setTarget(null);
			try {
				await onCancelled?.();
			} catch (err) {
				console.error(err);
			}
		} catch (err) {
			setCancelError(getRequestError(err, fallbackError));
		} finally {
			submitting.current = false;
		}
	};

	return {
		bookings: upcoming,
		loading,
		error,
		retry: () => setRevision((value) => value + 1),
		target,
		blocked,
		cancelError,
		cancelling,
		requestCancel,
		closeDialog,
		confirmCancel,
	};
}
